import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Platform } from '@app/classes';
import { AuthService } from '@app/services/auth.service';

/**
 * TimeTrackingProjectGuard determines if the current user can see projects
 */
@Injectable()
export class TimeTrackingProjectGuard {
    constructor(
        private auth: AuthService,
        private router: Router
    ) {}

    canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        const projectOwnerships = this.auth.employee?.activeTimeTrackingProjectOwnerships || [];
        const urlTree = this.router.parseUrl(state.url);

        /**
         * segments is an array of strings, hopefully taking the shape
         * ["time-tracking", "projects", "2", ...]
         */
        const segments: string[] = urlTree.root.children.primary.segments.map((s) => s.path); // Thank you Angular for such a clean API

        // someone has put the guard on an invalid route
        // we can't do anything
        if (segments.length < 2 || segments[0] !== 'time-tracking' || segments[1] != 'projects') {
            this.router.navigate(['/']);
            return false;
        }

        // if you are a time tracking admin, you can see anything
        if (this.auth.can(Platform.timeTracking.permission.view)) {
            return true;
        }

        // not a project owner
        if (projectOwnerships.length === 0) {
            this.router.navigate(['/']);
            return false;
        }

        // all project owners can see the index page
        const isProjectsIndexPage = segments.length === 2;
        if (isProjectsIndexPage) {
            return true;
        }

        const projectId = Number(segments[2]);

        // if the project id is Nan, that means we are on the create page (or some other future page that project owners
        // should not access). So, deny.
        if (Number.isNaN(projectId)) {
            this.router.navigate(['/']);
            return false;
        }

        // now it's a show or edit page,
        // so check if they own the project
        const ownsProject = projectOwnerships.some((ownership) => ownership.projectId === projectId);
        if (!ownsProject) {
            this.router.navigate(['/']);
            return false;
        }

        return true;
    }
}
